import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Gift, Send, Heart, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { SHOW_INFO, generateGiftWhatsAppUrl } from '../data/showData';

const Regalo = () => {
  const [formData, setFormData] = useState({
    nomeDestinatario: '',
    cognomeDestinatario: '',
    daChi: '',
    numBiglietti: '1',
    note: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.nomeDestinatario.trim() || !formData.cognomeDestinatario.trim() || !formData.daChi.trim()) {
      toast.error('Per favore compila tutti i campi obbligatori');
      return;
    }
    
    // Generate WhatsApp URL and redirect
    const whatsappUrl = generateGiftWhatsAppUrl(formData);
    window.location.href = whatsappUrl;
    
    toast.success('Perfetto! Ti sto reindirizzando su WhatsApp...');
  };

  const totalPrice = parseInt(formData.numBiglietti) * SHOW_INFO.ticketPrice;

  return (
    <div className="min-h-screen pt-24 pb-16 bg-[#0a0a0a]">
      <div className="container-custom max-w-4xl">
        {/* Back Link */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-gray-400 hover:text-[#FFD700] transition-colors mb-8"
          data-testid="back-to-home"
        >
          <ArrowLeft className="w-4 h-4" />
          Torna alla home
        </Link>

        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#FFD700]/10 mb-6">
            <Gift className="w-10 h-10 text-[#FFD700]" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Biglietto <span className="text-[#FFD700]">Regalo</span>
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Regala un'esperienza indimenticabile! Preparerò un biglietto personalizzato 
            con il nome del destinatario. Un pensiero originale che farà ridere e commuovere.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="gift-form">
              <div className="bg-[#121212] rounded-2xl border border-white/5 p-6 md:p-8 space-y-6">
                <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                  <Heart className="w-5 h-5 text-[#D946EF]" />
                  Chi riceverà il regalo?
                </h2>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="nomeDestinatario" className="text-gray-300">Nome destinatario *</Label>
                    <Input
                      id="nomeDestinatario"
                      name="nomeDestinatario"
                      value={formData.nomeDestinatario}
                      onChange={handleChange}
                      placeholder="Nome di chi riceve il regalo"
                      className="input-dark"
                      required
                      data-testid="input-nome-destinatario"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cognomeDestinatario" className="text-gray-300">Cognome destinatario *</Label>
                    <Input
                      id="cognomeDestinatario"
                      name="cognomeDestinatario"
                      value={formData.cognomeDestinatario}
                      onChange={handleChange}
                      placeholder="Cognome di chi riceve il regalo"
                      className="input-dark"
                      required
                      data-testid="input-cognome-destinatario"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="daChi" className="text-gray-300">Da chi è il regalo? *</Label>
                  <Input
                    id="daChi"
                    name="daChi"
                    value={formData.daChi}
                    onChange={handleChange}
                    placeholder="Il tuo nome o come vuoi firmarti"
                    className="input-dark"
                    required
                    data-testid="input-da-chi"
                  />
                  <p className="text-gray-500 text-sm">
                    Apparirà sul biglietto come "Da: [il tuo nome]"
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="numBiglietti" className="text-gray-300">Numero biglietti *</Label>
                  <Select 
                    value={formData.numBiglietti} 
                    onValueChange={(value) => handleSelectChange('numBiglietti', value)}
                  >
                    <SelectTrigger className="input-dark" data-testid="select-biglietti-regalo">
                      <SelectValue placeholder="Seleziona" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1a1a] border-white/10">
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                        <SelectItem 
                          key={num} 
                          value={num.toString()}
                          className="text-white hover:bg-white/10"
                        >
                          {num} {num === 1 ? 'biglietto' : 'biglietti'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="note" className="text-gray-300">Messaggio personalizzato (opzionale)</Label>
                  <Textarea
                    id="note"
                    name="note"
                    value={formData.note}
                    onChange={handleChange}
                    placeholder="Es: 'Buon compleanno!' o un messaggio speciale da includere"
                    className="input-dark min-h-[100px]"
                    data-testid="input-note-regalo"
                  />
                </div>
              </div>

              {/* Submit Button */}
              <Button 
                type="submit" 
                className="btn-gold w-full text-lg py-6 flex items-center justify-center gap-3"
                data-testid="submit-gift-btn"
              >
                <Send className="w-5 h-5" />
                Richiedi biglietto regalo
              </Button>

              <p className="text-center text-gray-500 text-sm">
                Ti contatterò su WhatsApp per i dettagli e il pagamento
              </p>
            </form>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Price Summary */}
            <div className="bg-[#121212] rounded-2xl border border-white/5 p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Riepilogo</h3>
              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-gray-400">
                  <span>Biglietto singolo</span>
                  <span>{SHOW_INFO.ticketPrice}€</span>
                </div>
                <div className="flex justify-between text-gray-400">
                  <span>Quantità</span>
                  <span>x{formData.numBiglietti}</span>
                </div>
                <div className="flex justify-between text-gray-400">
                  <span>Personalizzazione</span>
                  <span className="text-[#25D366]">Inclusa!</span>
                </div>
                <div className="border-t border-white/10 pt-3 flex justify-between text-white font-bold text-xl">
                  <span>Totale</span>
                  <span className="text-[#FFD700]">{totalPrice}€</span>
                </div>
              </div>
            </div>

            {/* Gift Preview */}
            <div className="bg-gradient-to-br from-[#1a1a1a] to-[#121212] rounded-2xl border border-[#FFD700]/30 p-6 relative overflow-hidden">
              <div className="absolute top-2 right-2">
                <Sparkles className="w-6 h-6 text-[#FFD700]/50" />
              </div>
              <h3 className="text-lg font-semibold text-[#FFD700] mb-4 flex items-center gap-2">
                <Gift className="w-5 h-5" />
                Il tuo biglietto regalo
              </h3>
              <div className="bg-[#0a0a0a] rounded-xl p-4 border border-white/10">
                <p className="text-center text-gray-400 text-sm mb-2">SISTER TAC</p>
                <p className="text-center text-white font-bold text-lg">
                  {formData.nomeDestinatario || 'Nome'} {formData.cognomeDestinatario || 'Cognome'}
                </p>
                <p className="text-center text-[#D946EF] text-sm mt-2">
                  Da: {formData.daChi || 'Chi regala'}
                </p>
                {formData.numBiglietti && (
                  <p className="text-center text-gray-500 text-xs mt-3">
                    {formData.numBiglietti} {parseInt(formData.numBiglietti) === 1 ? 'biglietto' : 'biglietti'}
                  </p>
                )}
              </div>
              <p className="text-gray-500 text-xs mt-4 text-center">
                Anteprima indicativa
              </p>
            </div>

            {/* Why Gift */}
            <div className="bg-[#D946EF]/10 rounded-2xl border border-[#D946EF]/30 p-6">
              <h3 className="text-lg font-semibold text-[#D946EF] mb-3">Perché regalare Sister Tac?</h3>
              <ul className="space-y-2 text-gray-300 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-[#FFD700]">✨</span>
                  Un regalo originale e memorabile
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#FFD700]">✨</span>
                  2 ore di risate garantite
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#FFD700]">✨</span>
                  Biglietto personalizzato con nome
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#FFD700]">✨</span>
                  Perfetto per compleanni e occasioni speciali
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Regalo;
